<template>
    <div>
        <card-header title="Poll Results" icon="fa-chart-bar"/>

        <card-body>
            <div class="p-4">
                <p class="has-text-grey mb-4">
                    <i class="fas fa-users mr-2"></i>{{ voterCount }} {{ voterCount === 1 ? 'person' : 'people' }} responded<span v-if="nonresponders.length">, {{ nonresponders.length }} outstanding</span>.
                    <span class="ml-2" v-if="closed"><i class="fas fa-lock mr-1"></i>Closed {{ pollClosesAt | moment('MMM Do, h:mma') }}</span>
                    <span class="ml-2" v-else-if="pollClosesAt"><i class="far fa-clock mr-1"></i>Closes {{ pollClosesAt | moment('MMM Do, h:mma') }}</span>
                </p>

                <!-- Free-form: there are no options to chart, just answers. -->
                <div v-if="isText">
                    <p class="has-text-grey is-italic" v-if="!votes.length">No responses yet.</p>
                    <table class="table is-narrow is-fullwidth" v-else>
                        <tbody>
                            <tr v-for="(voter, i) in votes" :key="i">
                                <td class="is-narrow"><strong>{{ voterName(voter) }}</strong></td>
                                <td class="answer-body">{{ voter.text_answer }}</td>
                                <td class="has-text-right has-text-grey is-size-7">{{ (voter.updated_at || voter.created_at) | moment('MMM Do, h:mma') }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- v-for on its own element: Vue 2 gives v-for priority over
                     v-if/v-else on the same tag, which collapses the branch. -->
                <template v-else>
                <div v-for="option in options" :key="option.id" class="mb-5">
                    <div class="columns is-gapless mb-1">
                        <div class="column">
                            <strong>{{ option.label }}</strong>
                            <b-tag size="is-small" class="ml-2" v-if="option.is_other">Write-in</b-tag>
                        </div>
                        <div class="column is-narrow has-text-grey">{{ option.votes }} · {{ percent(option) }}%</div>
                    </div>
                    <div class="bar-track">
                        <div class="bar" :style="{width: percent(option) + '%'}"></div>
                    </div>

                    <table class="table is-narrow is-fullwidth mt-2" v-if="votersFor(option.id).length">
                        <tbody>
                            <tr v-for="(voter, i) in votersFor(option.id)" :key="i">
                                <td>{{ voterName(voter) }}</td>
                                <td class="answer-body has-text-grey" v-if="option.is_other">{{ voter.text_answer }}</td>
                                <td class="has-text-right has-text-grey is-size-7">{{ (voter.updated_at || voter.created_at) | moment('MMM Do, h:mma') }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p class="has-text-grey is-italic is-size-7" v-else>No votes for this option.</p>
                </div>
                </template>

                <!-- The list staff actually work from: who still owes an answer. -->
                <div class="mt-5" v-if="nonresponders.length">
                    <p class="mb-2"><strong>Haven't answered</strong> <span class="has-text-grey">({{ nonresponders.length }})</span></p>
                    <b-tag v-for="(person, i) in nonresponders" :key="i" type="is-warning" class="mr-2 mb-1">
                        {{ voterName(person) }}
                    </b-tag>
                </div>
            </div>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <b-button type="is-primary" size="is-small" @click="exportCsv">
                <i class="fas fa-file-download mr-2"></i>Export CSV
            </b-button>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";

    import {client as http, apiUrl} from '@/http_client';
    import {mapGetters} from "vuex";

    export default {
        name: "AnnouncementResults",
        props: ['card', 'props'],
        components: {CardFooter, CardBody, CardHeader},
        data: function() {
            return {
                options: [],
                votes: [],
                nonresponders: [],
                pollType: null,
                pollClosesAt: null,
                voterCount: 0,
                apiUrl
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            isText: function() {
                return this.pollType === 'text';
            },
            closed: function() {
                return !!this.pollClosesAt && new Date(this.pollClosesAt).getTime() <= Date.now();
            }
        },
        methods: {
            load: function(force) {
                this.$emit('loading', true);
                http.get('/api/experience/posts/' + this.props.postId + '/votes', {force}).then(response => {
                    this.options = response.data.options;
                    this.votes = response.data.votes;
                    this.nonresponders = response.data.nonresponders || [];
                    this.pollType = response.data.poll_type;
                    this.pollClosesAt = response.data.poll_closes_at;
                    this.voterCount = response.data.voter_count;
                    this.$emit('loading', false);
                });
            },
            reload: function() {
                this.load(true);
            },
            // Percentages are of people who answered, not of votes cast — under
            // multi-select those are different numbers.
            percent: function(option) {
                if (!this.voterCount) return 0;
                return Math.round(option.votes / this.voterCount * 100);
            },
            votersFor: function(optionId) {
                return this.votes.filter(v => v.option_id === optionId);
            },
            voterName: function(voter) {
                return voter.preferred_name || [voter.first_name, voter.last_name].filter(Boolean).join(' ') || 'User #' + voter.user_id;
            },
            // Same authenticated-URL download the trip reports use.
            exportCsv: function() {
                window.open(
                    `${this.apiUrl}api/experience/posts/${this.props.postId}/votes/csv?auth=${this.currentUser.token}`,
                    '_blank');
            }
        },
        mounted() {
            this.load(true);
        }
    };
</script>

<style lang="scss" scoped>
    .bar-track {
        background-color: #f0f0f0;
        border-radius: 4px;
        height: 10px;
        overflow: hidden;

        .bar {
            background-color: #628b21;
            height: 100%;
            transition: width 0.3s ease;
        }
    }

    .answer-body {
        white-space: pre-wrap;
        word-break: break-word;
    }
</style>
